import HttpClient from "./httpClient";

export interface ExecutiveOverviewResponse {
  total_stale: number;
  assigned_stale: number;
  unassigned_stale: number;
}

export interface ScheduledCall {
  source_ref: string;
  ticket_id: string | number;
  agent_id: string | null;
  with_contact: string | null;
  start_at: string;
  end_at: string | null;
  subject: string | null;
  summary: string | null;
}

export interface TechnicianTicket {
  ticket_id: string | number;
  ticket_title: string;
  // Halo last_update timestamp (renamed from last_activity_at).
  last_update_at: string;
  // Halo lastactiondate — the delay/staleness anchor; may be null. The UI
  // computes days-delay from last_action_at ?? last_update_at.
  last_action_at: string | null;
  is_overdue: boolean;
  client_name: string;
  // Single string holding the two labelled sections ("Summary:" / "Next Steps:")
  // the classifier emits; the UI splits it for display (see insightFormat.ts).
  summary: string | null;
  notes: string | null;
  // Non-null when the last summary generation failed for this ticket; shown
  // red-tinted inline (any prior summary is still shown, greyed).
  summary_error: string | null;
  upcoming_calls: ScheduledCall[];
}

export interface TechnicianEntry {
  agent_id: string | null;
  agent_name: string;
  open_count: number;
  avg_age_days: number;
  upcoming_calls_count: number;
  tickets: TechnicianTicket[];
}

export type TechnicianOverviewResponse = TechnicianEntry[];

export interface SnoozedTicket {
  ticket_id: string | number;
  ticket_title: string;
  client_name: string;
  agent_name: string | null;
  last_update_at: string;
  snoozed_until: string;
  summary: string | null;
  notes: string | null;
  upcoming_calls: ScheduledCall[];
}

export interface TicketInsight {
  // notes is the stored per-ticket handoff note generated by the classifier — null when not yet generated.
  notes: string | null;
  // Single string with embedded "Summary:" / "Next Steps:" sections (see insightFormat.ts).
  summary: string;
  generated_at: string;
}

export interface UpcomingCall {
  subject: string;
  start_at: string;
  end_at: string;
  with_contact: string;
}

export interface TicketDetail {
  ticket_id: string;
  ticket_title: string;
  client_name: string;
  agent_id: string | null;
  agent_name: string | null;
  status: string;
  // Halo last_update timestamp (renamed from last_activity_at).
  last_update_at: string;
  // Halo lastactiondate — the delay/staleness anchor; may be null.
  last_action_at: string | null;
  waiting_days: number;
  is_overdue: boolean;
  snoozed_until: string | null;
  is_open: boolean;
  insight: TicketInsight | null;
  upcoming_calls: UpcomingCall[];
}

export interface SyncResult {
  tickets_upserted: number;
  appointments_upserted: number;
  insights_generated: number;
  structural_errored?: number;
  classifier_errored?: number;
  disabled?: boolean;
}

export interface SyncStatusPass {
  last_run_at: string | null;
  error: string | null;
  errored_count: number;
  state: 'idle' | 'syncing' | 'interrupted';
  heartbeat_at: string | null;
}

export interface SyncStatusLookback {
  // null since the poller dropped its fixed lookback window (pure forward cursor).
  lookback_days: number | null;
  last_activity_at: string | null;
  gap_days: number;
}

export interface SyncStatus {
  structural: SyncStatusPass;
  classifier: SyncStatusPass;
  lookback?: SyncStatusLookback;
  // MSP-wide: Halo denied appointment (calendar) access. Drives the localized
  // notice in the calls area instead of a global banner line.
  appointments_permission_denied?: boolean;
}

export interface StaleTicketConfig {
  stale_threshold_days: number;
  max_lookback_days: number;
  // Open statuses to track — numeric Halo status IDs (as strings, e.g. "1","2").
  // Stored server-side as a CSV of ids; the config UI selects by status id.
  open_status_ids: string[];
  // Ticket-type NAMES to include in overviews (INCLUSION list). Stored server-side
  // as a CSV of names; the config UI selects by type name. Empty = show nothing;
  // non-empty = show only these types.
  included_ticket_types: string[];
  enabled: boolean;
}

export interface StaleTicketConfigInput {
  stale_threshold_days: number;
  max_lookback_days: number;
  // Open statuses to track — numeric Halo status IDs (as strings, e.g. "1","2").
  // Stored server-side as a CSV of ids; the config UI selects by status id.
  open_status_ids: string[];
  // Ticket-type NAMES to include in overviews (INCLUSION list). Stored server-side
  // as a CSV of names; the config UI selects by type name. Empty = show nothing;
  // non-empty = show only these types.
  included_ticket_types: string[];
  enabled: boolean;
}

export interface HaloStatus {
  id: string;
  name: string;
}

export interface HaloTicketType {
  id: string;
  name: string;
}

export interface LookbackImpact {
  // true when max_lookback_days = 0 (no age limit)
  unlimited: boolean;
  // created_at cutoff (YYYY-MM-DD), or null when unlimited
  cutoff_date: string | null;
  // open past_tickets older than the cutoff that aren't already tracked;
  // null when it couldn't be computed (Halo/DB unavailable) — hide the warning
  excluded_open_count: number | null;
}

/**
 * Module-level memo for Halo statuses: cache the in-flight Promise so
 * concurrent callers all await the same single HTTP round-trip, and subsequent
 * opens are instant. Never resets after a successful fetch (statuses are
 * MSP-wide and stable for the lifetime of a browser session).
 */
let _haloStatusesPromise: Promise<HaloStatus[]> | null = null;

/**
 * Module-level memo for Halo ticket types — same caching contract as
 * _haloStatusesPromise (types are MSP-wide and stable for the session).
 */
let _haloTicketTypesPromise: Promise<HaloTicketType[]> | null = null;

interface ApiResponse<T> {
  status: "success" | "error";
  data: T;
  timestamp: string;
  error?: string;
}

function normalizeOpenStatusIds(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(String);
  const s = String(raw).trim();
  if (!s) return [];
  return s.split(',').map((v) => v.trim()).filter(Boolean);
}

// included_ticket_types round-trips as a CSV of NAMES; parse to a string[]
// (mirror normalizeOpenStatusIds).
function normalizeIncludedTicketTypes(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(String);
  const s = String(raw).trim();
  if (!s) return [];
  return s.split(',').map((v) => v.trim()).filter(Boolean);
}

class TicketLifecycleApi extends HttpClient {
  public getExecutiveOverview = async (): Promise<ExecutiveOverviewResponse> => {
    const response: ApiResponse<ExecutiveOverviewResponse> = await this.get(
      `/api/ticket-lifecycle/executive-overview`
    );
    return response.data;
  };

  public getTechnicianOverview = async (): Promise<TechnicianOverviewResponse> => {
    const response: ApiResponse<TechnicianOverviewResponse> = await this.get(
      `/api/ticket-lifecycle/technician-overview`
    );
    return response.data;
  };

  public snooze = async (ticketId: string | number, days?: number): Promise<void> => {
    await this.post(`/api/ticket-lifecycle/snooze`, {}, { ticket_id: ticketId, days: days ?? 7 });
  };

  public unsnooze = async (ticketId: string | number): Promise<void> => {
    await this.post(`/api/ticket-lifecycle/unsnooze`, {}, { ticket_id: ticketId });
  };

  public getSnoozed = async (): Promise<SnoozedTicket[]> => {
    const response: ApiResponse<SnoozedTicket[]> = await this.get(
      `/api/ticket-lifecycle/snoozed`
    );
    return Array.isArray(response.data) ? response.data : [];
  };

  public getTicketDetail = async (ticketId: string): Promise<TicketDetail> => {
    const response: ApiResponse<TicketDetail> = await this.get(
      `/api/ticket-lifecycle/ticket/${ticketId}`
    );
    return response.data;
  };

  public generateReplyDraft = async (ticketId: string): Promise<{ draft: string }> => {
    const response: ApiResponse<{ draft: string }> = await this.post(
      `/api/ticket-lifecycle/reply-draft`,
      {},
      { ticket_id: ticketId }
    );
    return response.data;
  };

  public sync = async (): Promise<SyncResult> => {
    const response: ApiResponse<SyncResult> = await this.post(
      `/api/ticket-lifecycle/sync`,
      {},
      {}
    );
    return response.data;
  };

  public getConfig = async (): Promise<StaleTicketConfig | null> => {
    const response: ApiResponse<StaleTicketConfig | null> = await this.get('/api/ticket-lifecycle/config');
    if (response.data == null) return null;
    return {
      ...response.data,
      max_lookback_days: Number(response.data.max_lookback_days) || 0,
      open_status_ids: normalizeOpenStatusIds(response.data.open_status_ids),
      included_ticket_types: normalizeIncludedTicketTypes(response.data.included_ticket_types),
      enabled: Boolean(response.data.enabled),
    };
  };

  public saveConfig = async (values: StaleTicketConfigInput): Promise<StaleTicketConfig> => {
    const response: ApiResponse<StaleTicketConfig> = await this.post('/api/ticket-lifecycle/config', {}, values);
    return {
      ...response.data,
      max_lookback_days: Number(response.data.max_lookback_days) || 0,
      open_status_ids: normalizeOpenStatusIds(response.data.open_status_ids),
      included_ticket_types: normalizeIncludedTicketTypes(response.data.included_ticket_types),
      enabled: Boolean(response.data.enabled),
    };
  };

  public getHaloStatuses = async (): Promise<HaloStatus[]> => {
    const response: ApiResponse<HaloStatus[]> = await this.get('/api/ticket-lifecycle/halo-statuses');
    return Array.isArray(response.data) ? response.data : [];
  };

  public getHaloTicketTypes = async (): Promise<HaloTicketType[]> => {
    const response: ApiResponse<HaloTicketType[]> = await this.get('/api/ticket-lifecycle/halo-ticket-types');
    return Array.isArray(response.data) ? response.data : [];
  };

  public getLookbackImpact = async (
    values: { max_lookback_days: number; open_status_ids: string[] }
  ): Promise<LookbackImpact> => {
    const response: ApiResponse<LookbackImpact> = await this.post('/api/ticket-lifecycle/lookback-impact', {}, values);
    return response.data;
  };

  public getHaloStatusesCached = (): Promise<HaloStatus[]> => {
    if (!_haloStatusesPromise) {
      _haloStatusesPromise = this.getHaloStatuses().catch((err) => {
        // Reset so a future open can retry; return empty array for current callers.
        _haloStatusesPromise = null;
        throw err;
      });
    }
    return _haloStatusesPromise;
  };

  public getHaloTicketTypesCached = (): Promise<HaloTicketType[]> => {
    if (!_haloTicketTypesPromise) {
      _haloTicketTypesPromise = this.getHaloTicketTypes().catch((err) => {
        // Reset so a future open can retry; return empty array for current callers.
        _haloTicketTypesPromise = null;
        throw err;
      });
    }
    return _haloTicketTypesPromise;
  };

  public getHaloBaseUrl = async (): Promise<string> => {
    const response: ApiResponse<string> = await this.get('/api/ticket-lifecycle/halo-base-url');
    return typeof response.data === 'string' ? response.data : '';
  };

  public getSyncStatus = async (): Promise<SyncStatus> => {
    const response: ApiResponse<SyncStatus> = await this.get('/api/ticket-lifecycle/sync-status');
    return response.data;
  };

  public generateNoteDraft = async (ticketId: string): Promise<{ draft: string }> => {
    const response: ApiResponse<{ draft: string }> = await this.post(
      `/api/ticket-lifecycle/note-draft`,
      {},
      { ticket_id: ticketId }
    );
    return response.data;
  };

  public postNote = async (args: {
    ticketId: string | number;
    note: string;
    isPrivate: boolean;
    timeTaken?: number;
    outcome?: string;
    statusId?: string | number;
  }): Promise<void> => {
    await this.post(`/api/ticket-lifecycle/post-note`, {}, {
      ticket_id: args.ticketId,
      note: args.note,
      is_private: args.isPrivate,
      time_taken: args.timeTaken,
      outcome: args.outcome,
      status_id: args.statusId,
    });
  };
}

/** Deep-link to a Halo PSA ticket (empty when the base URL is unknown). */
export function haloTicketUrl(baseUrl: string, ticketId: string | number): string {
  if (!baseUrl) return '';
  return `${baseUrl.replace(/\/+$/, '')}/tickets?id=${ticketId}`;
}

/** Theo's own ticket page (supervisor ticket detail). */
export function theoTicketUrl(ticketId: string | number): string {
  return `/supervisor/tickets/${ticketId}`;
}

const ticketLifecycleApi = new TicketLifecycleApi();

export default ticketLifecycleApi;
